import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  Typography,
  Space,
  Empty,
  Spin,
  Popconfirm,
  Modal,
  Form,
  message,
  Table,
  Tag,
  Switch,
  Tooltip,
  Select,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface ApiKey {
  id: number;
  provider: string;
  api_key: string;
  name: string;
  is_active: boolean;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const ApiKeyList: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());
  const [form] = Form.useForm();

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      message.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingKey(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (key: ApiKey) => {
    setEditingKey(key);
    form.setFieldsValue({
      name: key.name,
      api_key: key.api_key,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: { name: string; api_key: string; provider: string }) => {
    try {
      if (editingKey) {
        const response = await axios.put(`${API_URL}/api-keys/${editingKey.id}`, {
          name: values.name,
        });
        setApiKeys(apiKeys.map(k => k.id === editingKey.id ? response.data : k));
        message.success('API key updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/api-keys`, {
          api_key: values.api_key,
          name: values.name,
          provider: values.provider,
        });
        setApiKeys([response.data, ...apiKeys]);
        message.success('API key added successfully');
      }
      setModalVisible(false);
      form.resetFields();
    } catch (error: any) {
      console.error('Failed to save API key:', error);
      if (error.response?.data?.error === 'API key already exists') {
        message.error('This API key already exists');
      } else {
        message.error('Failed to save API key');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api-keys/${id}`);
      setApiKeys(apiKeys.filter(k => k.id !== id));
      message.success('API key deleted successfully');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      message.error('Failed to delete API key');
    }
  };

  const handleToggleActive = async (id: number, is_active: boolean) => {
    try {
      const response = await axios.put(`${API_URL}/api-keys/${id}`, { is_active });
      setApiKeys(apiKeys.map(k => k.id === id ? response.data : k));
      message.success(`API key ${is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Failed to update API key:', error);
      message.error('Failed to update API key');
    }
  };

  const toggleKeyVisibility = (id: number) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(id)) {
      newVisible.delete(id);
    } else {
      newVisible.add(id);
    }
    setVisibleKeys(newVisible);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const maskKey = (key: string) => {
    if (key.length <= 12) return '••••••••';
    return key.substring(0, 8) + '••••••••' + key.substring(key.length - 4);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name: string) => <Text strong>{name || '-'}</Text>,
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (key: string, record: ApiKey) => (
        <Space>
          <Text code style={{ fontSize: '12px' }}>
            {visibleKeys.has(record.id) ? key : maskKey(key)}
          </Text>
          <Tooltip title={visibleKeys.has(record.id) ? 'Hide' : 'Show'}>
            <Button
              type="text"
              size="small"
              icon={visibleKeys.has(record.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => toggleKeyVisibility(record.id)}
            />
          </Tooltip>
          <Tooltip title="Copy">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(key)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100,
      render: (provider: string) => (
        <Tag color={provider === 'gemini' ? 'orange' : 'blue'}>{provider.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: boolean, record: ApiKey) => (
        <Switch
          checked={is_active}
          onChange={(checked) => handleToggleActive(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: ApiKey) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete API key"
            description="Are you sure you want to delete this API key?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <KeyOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          API Keys
        </Title>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Add API Key
        </Button>
      </div>

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Tag color="green">{apiKeys.filter(k => k.is_active).length} Active</Tag>
            <Tag color="default">{apiKeys.filter(k => !k.is_active).length} Inactive</Tag>
            <Tag color="blue">{apiKeys.filter(k => k.provider === 'xai').length} XAI</Tag>
            <Tag color="orange">{apiKeys.filter(k => k.provider === 'gemini').length} Gemini</Tag>
          </Space>
        </div>

        {apiKeys.length === 0 ? (
          <Empty
            image={<KeyOutlined style={{ fontSize: '64px', color: '#ccc' }} />}
            description={
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: '16px' }}>No API keys yet</Text>
                <Text type="secondary">Add your first API key to get started.</Text>
              </Space>
            }
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add API Key
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={apiKeys}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            size="middle"
          />
        )}
      </Card>

      <Modal
        title={
          <Space>
            {editingKey ? <EditOutlined /> : <PlusOutlined />}
            {editingKey ? 'Edit API Key' : 'Add API Key'}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: '16px' }}
        >
          <Form.Item
            name="name"
            label="Name (optional)"
          >
            <Input placeholder="Enter a name for this key" size="large" />
          </Form.Item>

          {!editingKey && (
            <>
              <Form.Item
                name="provider"
                label="Provider"
                initialValue="xai"
                rules={[{ required: true, message: 'Please select a provider' }]}
              >
                <Select size="large">
                  <Select.Option value="xai">XAI</Select.Option>
                  <Select.Option value="gemini">Gemini</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="api_key"
                label="API Key"
                rules={[{ required: true, message: 'Please enter the API key' }]}
              >
                <Input.TextArea
                  placeholder="Enter the API key"
                  rows={3}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingKey ? 'Update' : 'Add'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApiKeyList;
